import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { triplePointsEndDate } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeover.constants'
import TriplePointTakeoverCampaignPill from './TriplePointTakeoverCampaignPill'
import { INTL_LONG_DATE } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

interface Props {
  heroTakeover?: boolean
  mobileCompact?: boolean
  textColour?: App.Design.ContentColour
  viewOffersTo?: string
}

/**
 * Shared subtitle
 */
function TriplePointTakeoverCampaignSubtitle(props: Props) {
  const {
    heroTakeover,
    mobileCompact,
    textColour: textColourOverride,
    viewOffersTo,
  } = props
  const intl = useIntl()
  const [tier] = useCurrentLoyaltyTier()
  const textColour = textColourOverride ?? `tier-${tier}-contrast`

  const formattedEndDate = useMemo(() => {
    const endDate = new Date(triplePointsEndDate)
    if (Number.isNaN(endDate.getTime())) return undefined

    return intlDateFormatter(endDate, INTL_LONG_DATE, intl.locale)
  }, [intl.locale])

  const viewAllOffersText = intl.formatMessage({
    id: 'uiS77hbzDzRjhnTehe9M',
    defaultMessage: 'View all offers',
  })

  const renderViewOffersLink = useCallback((chunks: ReactNode) => {
    return <TextLink key="view-all-offers-link" to={viewOffersTo} wrap="no-wrap">{chunks}</TextLink>
  }, [viewOffersTo])

  const defaultPill = <TriplePointTakeoverCampaignPill />
  let subtitle: ReactNode

  if (!formattedEndDate) {
    if (viewOffersTo) {
      subtitle = <FormattedMessage
        id="dekrdn_8PTXITD2_ltZX"
        defaultMessage="Look for {pill} on select packages! Hurry, limited time only. <link>View all offers</link>"
        
        values={{
          pill: defaultPill,
          link: renderViewOffersLink,
        }}
      />
    } else {
      subtitle = <FormattedMessage
        id="eriI4lOs8CoS1t6O0VMD"
        defaultMessage="Look for {pill} on select packages! Hurry, limited time only."
        
        values={{
          pill: defaultPill,
        }}
      />
    }
  } else if (viewOffersTo) {
    subtitle = <FormattedMessage
      id="YOpJHLKxVcjpA3SunZ4n"
      defaultMessage="Look for {pill} on select packages! Hurry, valid until {date}. <link>View all offers</link>"
      
      values={{
        pill: defaultPill,
        date: formattedEndDate,
        link: renderViewOffersLink,
      }}
    />
  } else {
    subtitle = <FormattedMessage
      id="jJP7ENZH9tM8dAFcNbM1"
      defaultMessage="Look for {pill} on select packages! Hurry, valid until {date}."
      
      values={{
        pill: defaultPill,
        date: formattedEndDate,
      }}
    />
  }

  if (!heroTakeover && !mobileCompact) {
    return <BodyText variant="large" colour={textColour}>
      {subtitle}
    </BodyText>
  }

  return <>
    <CSSBreakpoint max="mobile">
      {heroTakeover && <BodyText variant="large" colour={textColour}>
        {viewOffersTo && <FormattedMessage
          id="gV1_rJtJfneRnnmFh4Lc"
          defaultMessage="Triple Société points on select offers. <link>View all</link>"
          
          values={{
            link: renderViewOffersLink,
          }}
        />}
        {!viewOffersTo && <FormattedMessage
          id="h71sOpBYdsEQDEH91jnE"
          defaultMessage="Triple Société points on select offers."
          
        />}
      </BodyText>}
      {!heroTakeover && <Group direction="horizontal" gap={8} wrap="wrap">
        <BodyText variant="large" colour={textColour}>
          <FormattedMessage
            id="tpVxc4gzulVYQo-A_0z7"
            defaultMessage="Look for {pill}"
            
            values={{
              pill: defaultPill,
            }}
          />
        </BodyText>
        {viewOffersTo && <TextLink
          size="large"
          to={viewOffersTo}
          wrap="no-wrap"
          weight="regular"
        >
          {viewAllOffersText}
        </TextLink>}
      </Group>}
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <BodyText variant="large" colour={textColour}>
        {subtitle}
      </BodyText>
    </CSSBreakpoint>
  </>
}

export default TriplePointTakeoverCampaignSubtitle
